import type { AskStreamEvent, Citation, UUID } from './types';

/**
 * Accumulated state of one streamed answer, folded from the SSE events
 * (meta → delta* → done). Shared by the Knowledge chat and the Shiba widget so
 * the event contract → state mapping lives in exactly one place; each surface
 * embeds these fields in its own message/entry shape.
 */
export interface AskStreamAccum {
  status: 'pending' | 'streaming' | 'done';
  answer: string;
  sources: Citation[];
  /** Null on a turn read back from the history, which records no score. */
  confidence: number | null;
  /** Null until `done` — only persisted answers can receive feedback. */
  interactionId: UUID | null;
}

export function emptyAskStreamAccum(): AskStreamAccum {
  return { status: 'pending', answer: '', sources: [], confidence: 0, interactionId: null };
}

/** The fields the fold touches — surfaces may widen `status` with 'error'. */
type Foldable = Omit<AskStreamAccum, 'status'> & { status: string };

/**
 * Fold one SSE event into the accumulated answer state. Generic so each surface
 * folds events into its own entry shape (which adds an 'error' status); the cast
 * is safe — the fold only ever writes {@link AskStreamAccum} fields.
 */
export function applyAskStreamEvent<T extends Foldable>(state: T, event: AskStreamEvent): T {
  switch (event.type) {
    case 'meta':
      // Status stays 'pending' until the first delta, so the skeleton holds
      // while only provenance has arrived.
      return { ...state, sources: event.citations, confidence: event.confidence };
    case 'delta':
      return { ...state, status: 'streaming', answer: state.answer + event.text } as T;
    case 'done':
      return { ...state, status: 'done', interactionId: event.interactionId } as T;
  }
}
